import { io, Socket } from 'socket.io-client'
import { currentAccessToken } from '../store/authStore'

const BACKEND_URL = (import.meta.env.VITE_API_URL ?? 'http://localhost:5000/api').replace(/\/api$/, '')

let socket: Socket | null = null
/** The collectors the map is following, asked for again after every reconnect. */
const followed = new Set<string>()

export function connectTracker(token: string): Socket {
  // Connected, or still connecting or reconnecting on its own: keep it.
  if (socket?.active) return socket
  // A socket the server refused (a token it no longer accepts) never tries
  // again by itself, so it is replaced rather than left beside a new one.
  socket?.disconnect()

  socket = io(`${BACKEND_URL}/tracker`, {
    // Read at every connection attempt, so a reconnect after a token refresh
    // or a password change sends the current token, not the one the map
    // opened with.
    auth: (cb) => cb({ token: currentAccessToken() ?? token }),
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionDelay: 3000,
  })

  const opened = socket
  socket.on('connect', () => {
    console.log('[Admin Socket] Connected to tracker')
    // The server forgets a socket's rooms when it reconnects, so the map used
    // to stop receiving positions after any reconnect.
    for (const collectorId of followed) opened.emit('subscribe:collector', { collectorId })
  })

  socket.on('disconnect', (reason) => {
    // The server dropped this socket (its sweep does after a password change
    // in another tab). Socket.IO does not retry that by itself; the next
    // handshake reads the current token.
    if (reason === 'io server disconnect') {
      setTimeout(() => {
        if (socket === opened && !opened.connected) opened.connect()
      }, 3000)
    }
  })

  socket.on('connect_error', (err) => {
    console.warn('[Admin Socket] Connection error:', err.message)
  })

  return socket
}

export function subscribeToCollector(collectorId: string) {
  followed.add(collectorId)
  socket?.emit('subscribe:collector', { collectorId })
}

export function unsubscribeFromCollector(collectorId: string) {
  followed.delete(collectorId)
  socket?.emit('unsubscribe:collector', { collectorId })
}

export function onCollectorLocation(
  collectorId: string,
  callback: (data: { collectorId: string; lat: number; lng: number; timestamp: string }) => void,
) {
  socket?.on(`location:${collectorId}`, callback)
  return () => {
    socket?.off(`location:${collectorId}`, callback)
  }
}

export function disconnectTracker() {
  followed.clear()
  socket?.disconnect()
  socket = null
}

export function getSocket(): Socket | null {
  return socket
}
